import { useSearchParams } from 'next/navigation';
import { useEffect } from 'react';

import { differenceInCalendarDays, formatISO } from 'date-fns';

import {
  selectConfigServiceFeatureFlags,
  selectConfigServiceFeatureUplift,
  type TConfigServiceUplift,
} from '@/ducks/common/settings';
import { FILTER_KEY } from '@/ducks/filters/types';
import { selectInvoiceCurrencyCode } from '@/ducks/pages/summary/selectors';
import { currentNYTime } from '@/helpers/util/dateUtil';
import { type TCurrencyCode, type TISODate } from '@/infra/types/common';
import { useAppSelector } from '@/store';

import { loadUplift } from './adapter';
import { selectRelevantCurrencyCode, selectUpliftConfig, selectUpliftSupportedCurrencies } from './selectors';

export const useInitUplift = (pathname: string) => {
  const isEnabled = Boolean(useAppSelector(selectConfigServiceFeatureFlags)?.uplift);
  const config = useAppSelector(selectUpliftConfig);
  const relevantCurrencyCode = useAppSelector(selectRelevantCurrencyCode);
  const currencyCodeFromInvoice = useAppSelector(selectInvoiceCurrencyCode);
  const searchParams = useSearchParams();

  const isFixedCurrency = pathname.includes('/payment') || pathname.includes('/confirmation');
  const currencyCode = isFixedCurrency ? currencyCodeFromInvoice : relevantCurrencyCode;
  const isCurrencyReady = isFixedCurrency || currencyCode === searchParams.get(FILTER_KEY.currencyCode);

  useEffect(() => {
    if (isCurrencyReady) loadUplift({ config, currencyCode, isEnabled });
  }, [config, currencyCode, isCurrencyReady, isEnabled]);
};

export type TUseIsUpliftApplicable = {
  actualDate?: TISODate;
  amount: number;
  currencyCode: TCurrencyCode;
  startDate: TISODate;
};

export const useIsUpliftApplicable = ({ actualDate, amount, currencyCode, startDate }: TUseIsUpliftApplicable) => {
  const isUpliftEnabled = useAppSelector(selectConfigServiceFeatureUplift);
  const currencies = useAppSelector(selectUpliftSupportedCurrencies);
  const config = useAppSelector(selectUpliftConfig) as TConfigServiceUplift;

  if (!currencyCode || !startDate || !(amount > 0)) return false;

  if (!isUpliftEnabled) return false;

  if (!currencies.includes(currencyCode)) return false;

  const { maximumPrice, minimumDays, minimumPrice } = config || {};
  const minPrice = minimumPrice?.[currencyCode];
  const maxPrice = maximumPrice?.[currencyCode];
  const minDays = minimumDays?.[currencyCode];

  if ((minPrice > 0 && amount < minPrice) || (maxPrice > 0 && maxPrice < amount)) return false;

  if (minDays > 0) {
    const currentDate = actualDate || getCurrentUpliftDate();
    if (differenceInCalendarDays(startDate, currentDate) < minDays) return false;
  }

  return true;
};

const getCurrentUpliftDate = (): TISODate => formatISO(currentNYTime(new Date()), { representation: 'date' });
